import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/shared/lib/logger";

/**
 * Lean auth context: session, user, and a single `loading` flag.
 *
 * Intentionally NOT included here:
 * - isAdmin → use `useAdminRole()` (lazy useQuery)
 * - account_status → use `useAccountStatus()` (lazy useQuery)
 *
 * Why split? An async RPC for role lookup must never block the entire app
 * from rendering protected content. The session check is fast and synchronous
 * after the initial Supabase hydration; role/status checks are not.
 */

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** True until the initial getSession() resolves. */
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only commit session state when something meaningful actually changed.
    // Supabase fires TOKEN_REFRESHED (~hourly + on tab focus) and sometimes
    // re-fires SIGNED_IN; treating each event as a state change creates a new
    // session object reference, re-rendering every useAuth() consumer and
    // re-running effects keyed on `user` — which feels like the page is
    // constantly refreshing.
    const applySession = (next: Session | null) => {
      setSession((prev) => {
        if (prev === next) return prev;
        if (!prev && !next) return prev;
        if (
          prev &&
          next &&
          prev.access_token === next.access_token &&
          prev.user?.id === next.user?.id
        ) {
          return prev;
        }
        return next;
      });
    };

    // CRITICAL: register the listener BEFORE calling getSession() to avoid
    // missing the very first SIGNED_IN event during hydration.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        logger.log("[AuthProvider] auth event:", event);
        applySession(nextSession);
      }
    );

    supabase.auth.getSession().then(({ data: { session: initial }, error }) => {
      if (error) {
        logger.error("[AuthProvider] getSession error:", error);
      }
      applySession(initial ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Memoize the context value so consumers don't re-render on every parent
  // render. Identity only changes when session or loading actually change.
  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      signOut,
    }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
};
